/**
 * A QR code for a URL, drawn as inline SVG (no canvas, no network), plus the
 * same SVG as a download for flyers and posters.
 *
 * Encoding is `toqr` (MIT, ~14 KB, no dependencies; already in the lockfile
 * through @expo/cli): it returns the module matrix row by row, 1 = dark. Error
 * correction M survives a smudged print; the 4-module quiet zone is the
 * spec's minimum, so scanners find the code on any background.
 */
import { useMemo } from 'react';
import { toQR } from 'toqr';

const QUIET = 4;

/** The SVG markup for `text`, black on white whatever the theme (scanners need contrast). */
export function qrSvg(text: string): string {
  const cells = toQR(text);
  const n = Math.round(Math.sqrt(cells.length));
  let d = '';
  for (let y = 0; y < n; y += 1) {
    for (let x = 0; x < n; x += 1) {
      if (cells[y * n + x]) d += `M${x + QUIET} ${y + QUIET}h1v1h-1z`;
    }
  }
  const size = n + QUIET * 2;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges">` +
    `<rect width="${size}" height="${size}" fill="#fff"/><path d="${d}" fill="#000"/></svg>`
  );
}

export function QrCode({ text, label, size = 132 }: { text: string; label: string; size?: number }) {
  const svg = useMemo(() => qrSvg(text), [text]);
  return (
    <img
      className="qr"
      src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`}
      width={size}
      height={size}
      alt={label}
    />
  );
}

/** A link that saves the QR code as an SVG file. */
export function QrDownload({ text, filename, children = 'Download QR code' }: { text: string; filename: string; children?: string }) {
  const svg = useMemo(() => qrSvg(text), [text]);
  return (
    <a className="btn btn-sm btn-ghost" href={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`} download={filename}>
      {children}
    </a>
  );
}
